import React from 'react';
import * as yup from 'yup';
import SimpleModal from '$/components/ui/SimpleModal';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { CloseIcon, DragHandleIcon, TrashIcon } from '$/images/Icons';
import { AddIcon } from '$/images/FontAwesomeIcons';
import { css } from '@emotion/react';
import tw from 'twin.macro';
import ApolloForm from '$/components/ApolloForm';
import { OptionFieldInput, OptionField, SelectionType } from '$/GraphQL/schema/schema';
import { DragContainer, Draggable } from '$/components/ui/DragAndDrop';
import stripTypenames from '$/utils/stripTypenames';
import pluralize from 'pluralize';
import Dropdown from '$/components/ui/Input/Basics/Dropdown';
import allUSStates from '$/components/ui/Input/Location/States.json';
import countries from '$/shared/frontend/Countries.json';
import Button from '$/components/ui/Button';
import Form from '$/components/ui/Form';
import Input from '$/components/ui/Input/Basics/Input';

const minRange = 2;

const addOptionsDropdownOptions: {
  value: string;
  text: string;
  options: OptionFieldInput[];
}[] = [
  {
    value: 'USStateCodes',
    text: 'List of State codes in USA',
    options: Object.entries(allUSStates).map(([code, name]) => ({
      text: name,
      value: code,
    })),
  },
  {
    value: 'USStateNames',
    text: 'List of State names in USA',
    options: Object.entries(allUSStates).map(([_, name]) => ({
      text: name,
      value: name,
    })),
  },
  {
    value: 'CountryCodes',
    text: 'List of country codes',
    options: Object.entries(countries).map(([code, name]) => ({
      text: name,
      value: code,
    })),
  },
  {
    value: 'CountryNames',
    text: 'List of country names',
    options: Object.entries(countries).map(([_, name]) => ({
      text: name,
      value: name,
    })),
  },
];

const AddOptionsDropdown: React.FC<{
  onAdd: (options: OptionFieldInput[]) => void;
}> = ({ onAdd }) => {
  const [selected, setSelected] = React.useState<string>();

  const onChange = React.useCallback((v) => {
    setSelected(v);
  }, []);
  const onSubmit = React.useCallback(() => {
    if (!selected) return;
    const options = addOptionsDropdownOptions.find(({ value }) => value === selected)?.options;
    if (!options) return;
    onAdd(options);
  }, [selected, onAdd]);

  return (
    <div className='flex flex-row items-center'>
      <Dropdown
        className='min-w-12e'
        options={addOptionsDropdownOptions}
        placeholder='Load choices...'
        onChange={onChange}
      />
      <Button.rounded className='ml-2' onClick={onSubmit}>
        Load
      </Button.rounded>
    </div>
  );
};

const CustomDropdownChoice: React.FC<{ isChanging: (options: OptionFieldInput[]) => void }> = ({
  isChanging,
}) => {
  const { control, formState, watch } = useFormContext();
  const { fields, append, move, remove } = useFieldArray({
    control,
    name: 'options', // unique name for your Field Array
  });

  const onAdd = React.useCallback(
    (options: OptionFieldInput[]): void => {
      append(options);
    },
    [append]
  );

  const onClear = React.useCallback((): void => {
    remove();
  }, [remove]);

  const onMove = ({
    removedIndex,
    addedIndex,
  }: {
    removedIndex: number | null;
    addedIndex: number | null;
  }): void => {
    if (removedIndex !== null && addedIndex !== null) {
      move(removedIndex, addedIndex);
      isChanging(watch('options'));
    }
  };

  const handleAddOption = (): void => {
    const option = {
      text: '',
      value: '',
    };
    append(option);
  };

  if (!fields) return null;

  return (
    <div
      className='-ml-2'
      css={css`
        .reorder-icon {
          opacity: 0;
          transition: opacity 0.3s;
        }
        &:hover {
          .reorder-icon {
            cursor: ns-resize;
            opacity: 1;
          }
        }
      `}
    >
      <DragContainer lockAxis='y' nonDragAreaSelector='.input' onDrop={onMove}>
        {fields.map((choice, index) => (
          <Draggable key={index}>
            <div
              key={choice.id}
              className={`w-full flex flex-row items-center ${
                formState.isSubmitted &&
                (!watch(`options.${index}.text`) || !watch(`options.${index}.value`))
                  ? 'mb-3_5e'
                  : 'mb-1e'
              }`}
            >
              <DragHandleIcon className='reorder-icon h-1_25e w-1e mr-1/2e text-gray-1' />
              <div
                className='flex items-center'
                css={css`
                  width: calc(100% - 2rem);
                `}
              >
                <div className='relative w-full flex'>
                  <div
                    className='mr-1e'
                    css={css`
                      width: calc(100% - 12rem);
                    `}
                  >
                    <Form.RawField
                      name={`options.${index}.text`}
                      className='mb-0'
                      component={
                        <Input
                          css={css`
                            input {
                              ${tw`transition-all duration-200`}
                              background-color: rgba(255, 255, 255, 0.4) !important;
                              &:focus {
                                background-color: rgba(255, 255, 255, 0.8) !important;
                              }
                            }
                          `}
                          placeholder={`Choice #${index + 1}`}
                        />
                      }
                      defaultValue={choice.text || ''}
                    />
                    <Form.FieldError
                      name={`options.${index}.text`}
                      css={css`
                        position: absolute !important;
                        margin-top: 0.75rem !important;
                      `}
                    />
                  </div>
                  <div className='w-12e'>
                    <Form.RawField
                      name={`options.${index}.value`}
                      className='mb-0'
                      component={
                        <Input
                          css={css`
                            input {
                              ${tw`transition-all duration-200`}
                              background-color: rgba(255, 255, 255, 0.4) !important;
                              &:focus {
                                background-color: rgba(255, 255, 255, 0.8) !important;
                              }
                            }
                          `}
                          placeholder={`Value`}
                        />
                      }
                      defaultValue={choice.value || ''}
                    />
                    <Form.FieldError
                      name={`options.${index}.value`}
                      css={css`
                        position: absolute !important;
                        margin-top: 0.75rem !important;
                      `}
                    />
                  </div>

                  {fields.length > minRange && (
                    <a
                      onClick={(e): void => {
                        e.preventDefault();
                        remove(index);
                      }}
                      className='appearence-none absolute bg-coral-1 rounded-full w-1_5e h-1_5e mb-2 -mr-2 flex items-center justify-center text-white'
                      css={css`
                        right: 0;
                        bottom: 1px;
                      `}
                      href='#'
                    >
                      <CloseIcon
                        css={css`
                          height: 9px !important;
                        `}
                      />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </Draggable>
        ))}
      </DragContainer>

      <div className='flex flex-row justify-between mt-1e'>
        <Button.blueText
          onClick={(): void => {
            handleAddOption();
          }}
          id='btnAddPollOption'
          className='text-md ml-1_5e'
        >
          <AddIcon className='mr-1/2e h-1_5e' />
          Add an option
        </Button.blueText>
        <AddOptionsDropdown onAdd={onAdd} />
        <Button.redText onClick={onClear} id='btnAddPollOption' className='text-md ml-1_5e'>
          <TrashIcon className='mr-1/2e h-1_5e' />
          Clear all
        </Button.redText>
      </div>
    </div>
  );
};

interface Props {
  title: string;
  fieldIndex: number;
  subType?: SelectionType;
  options?: OptionField[] | null;
  onSave: (index: number, subType: SelectionType, data: OptionFieldInput[]) => void;
  onClose: () => void;
}

const DropdownEditModal: React.FC<Props> = ({
  title,
  fieldIndex,
  subType,
  options,
  onSave,
  onClose,
}) => {
  const [changed, setChanged] = React.useState(false);
  const defaultValues = {
    subType: subType || SelectionType.Single,
    options:
      options && options.length > 0
        ? stripTypenames(options)
        : new Array(minRange).fill('').map((_) => {
            return { text: '', value: '' } as OptionField;
          }),
  };
  const isEdit = options && options.length > 0;

  const isChanging = (options: OptionFieldInput[]): void => {
    setChanged(JSON.stringify(defaultValues.options) !== JSON.stringify(options));
  };

  return (
    <ApolloForm
      showUnsavedChangesDialog
      defaultValues={defaultValues}
      validationSchema={yup.object().shape({
        options: yup
          .array(
            yup.object().shape({
              text: yup.string().trim().required(),
            })
          )
          .required()
          .min(minRange, `You must have at least ${pluralize('option', minRange, true)}`),
      })}
      onSubmit={async (data: {
        id?: string;
        subType: SelectionType;
        options: OptionFieldInput[];
      }): Promise<void> => {
        if (
          defaultValues.subType !== data.subType ||
          JSON.stringify(defaultValues.options) !== JSON.stringify(data.options)
        ) {
          onSave(fieldIndex, data.subType, data.options);
        }
        onClose();
      }}
    >
      <SimpleModal
        title={
          <span>
            Custom selector choices for:
            <br />
            {title}
          </span>
        }
        className='min-w-40e'
        onCancel={(): void => onClose()}
        cancel={<Button>Cancel</Button>}
        submit={<Form.SubmitButton cleanText={isEdit && !changed ? 'Close' : 'Save'} />}
      >
        <div className='pl-4 pr-2 '>
          <Form.FieldLabel title={<div className='flex'>Selection type:</div>} />
          <Form.Field
            name='subType'
            component={
              <Dropdown
                direction='left'
                options={[
                  { value: SelectionType.Single, text: 'Single selection' },
                  { value: SelectionType.Multi, text: 'Multi selection' },
                ]}
              />
            }
          />
        </div>
        <CustomDropdownChoice isChanging={isChanging} />
      </SimpleModal>
    </ApolloForm>
  );
};

export default DropdownEditModal;
